import { ChessPiece } from "../models/base-model";

const sortByValue = (pieces: ChessPiece[]): ChessPiece[] =>
    pieces.toSorted((a, b) => ChessPiece.value(a) - ChessPiece.value(b));

type CalculateExchangeProps = {
    attackers: ChessPiece[];
    defenders: ChessPiece[];
    target: ChessPiece | undefined;
};

/**
 * Calculates the net material gain/loss resulting from a sequence of captures on a square,
 * using a Static Exchange Evaluation (SEE) algorithm.
 *
 * The algorithm alternates attackers and defenders, recursively simulating each capture in value order,
 * then backtracks to determine the optimal net gain for the side to move.
 *
 * @returns {number} The best net material gain for the side initiating the exchange (positive if advantageous, negative if not).
 */
const calculateExchange = ({
    attackers,
    defenders,
    target,
}: CalculateExchangeProps): number => {
    if (!target) {
        return 0;
    }

    const attackerValues = sortByValue(attackers).map(ChessPiece.value);
    const defenderValues = sortByValue(defenders).map(ChessPiece.value);

    const see = (
        attackers: number[],
        defenders: number[],
        toCapture: number
    ): number => {
        if (attackers.length === 0) {
            return 0;
        }
        const attackerValue = attackers[0];
        const restAttackers = defenders;
        const restDefenders = attackers.slice(1);
        const gain =
            toCapture - see(restAttackers, restDefenders, attackerValue!);
        return Math.max(0, gain);
    };

    return see(attackerValues, defenderValues, ChessPiece.value(target));
};

export const ExchangeUtil = {
    calculateExchange,
};
